import { movies, type Movie } from "@/mocks/movies";
import { news, type NewsItem } from "@/mocks/news";

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((m) => m.id === id);
}

export function getNewsById(id: string | undefined): NewsItem | undefined {
  if (!id) return undefined;
  return news.find((n) => n.id === id);
}

export function getRelatedMovies(current: Movie, limit = 6): Movie[] {
  return movies
    .filter((m) => m.id !== current.id)
    .map((m) => {
      let score = 0;
      if (m.type === current.type) score += 3;
      score += m.genres.filter((g) => current.genres.includes(g)).length * 2;
      score += m.actors.filter((a) => current.actors.includes(a)).length * 2;
      if (m.region === current.region) score += 1;
      return { m, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.m);
}

export function getRelatedNews(current: NewsItem, limit = 4): NewsItem[] {
  const sameCategory = news.filter((n) => n.id !== current.id && n.category === current.category);
  const others = news.filter((n) => n.id !== current.id && n.category !== current.category);
  return [...sameCategory, ...others].slice(0, limit);
}

export interface MovieFilter {
  type: string;
  genre?: string;
  region?: string;
  year?: string;
  sort?: string;
}

export function filterMovies(filter: MovieFilter): Movie[] {
  const list = movies.filter((m) => {
    if (m.type !== filter.type) return false;
    if (filter.genre && filter.genre !== "全部" && !m.genres.includes(filter.genre)) return false;
    if (filter.region && filter.region !== "全部" && m.region !== filter.region) return false;
    if (filter.year && filter.year !== "全部") {
      if (filter.year === "更早") {
        if (m.year >= 2023) return false;
      } else if (String(m.year) !== filter.year) {
        return false;
      }
    }
    return true;
  });

  const sort = filter.sort || "updated";
  return list.sort((a, b) => {
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "year") return b.year - a.year;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
}

export function latestMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, limit);
}

export function latestNews(limit = 5): NewsItem[] {
  return [...news].sort((a, b) => b.publishDate.localeCompare(a.publishDate)).slice(0, limit);
}

export interface SearchResult {
  movies: Movie[];
  news: NewsItem[];
}

export function searchAll(keyword: string): SearchResult {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return { movies: [], news: [] };
  const matchMovie = movies.filter((m) =>
    [m.title, m.director, ...m.actors, ...m.genres, m.region, String(m.year)]
      .join(" ")
      .toLowerCase()
      .includes(kw)
  );
  const matchNews = news.filter((n) =>
    [n.title, n.summary, n.author, ...n.content].join(" ").toLowerCase().includes(kw)
  );
  return { movies: matchMovie, news: matchNews };
}

export function formatRating(rating: number): string {
  return rating.toFixed(1);
}